/*
 * usePlannerVoyage
 *
 * The planner's voyage context, derived from the RouteStore: the vessel's
 * active planned voyage, its active (else newest suggested) route and that
 * route's points. Loads what the store is missing; Fleet Explorer lifecycle
 * changes land here through the store, without a refetch.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { coreGet } from "@/services/coreApi";
import { useAccessContext } from "@/contexts/AccessContext";
import { useRouteStore } from "@/features/route/state/RouteStore.jsx";
import { normalizeTreeMetadata } from "@/features/route/state/RouteAdapters.jsx";
import {
  isActiveEntity,
  isPlannedEntity,
} from "@/features/route/state/RouteState.jsx";
import {
  selectActiveOrSuggestedRoute,
  selectRoutePointsGeojson,
  selectRoutesForVoyage,
  selectVoyagesForVessel,
  toId,
} from "@/features/route/state/RouteSelectors.jsx";
import { useTreeRouteWaypoints } from "@/features/route/hooks/useTreeRouteWaypoints.jsx";

const EMPTY_IDS = [];

export default function usePlannerVoyage({
  vesselId,
  enabled = true,
  refreshSignal = 0,
}) {
  const { accessContext, setAccessContext } = useAccessContext();
  const { state, upsertEntities, setRoutesForVoyage } = useRouteStore();
  const parsedVesselId = toId(vesselId);
  const [voyagesLoadedFor, setVoyagesLoadedFor] = useState(null);
  const [voyagesFailedFor, setVoyagesFailedFor] = useState(null);

  // Read through a ref so a token refresh doesn't refetch (the loaders have no fetched-gate).
  const accessRef = useRef(null);
  accessRef.current = { accessContext, setAccessContext };

  // Additive on purpose: the tree owns the vessel's voyage list (months
  // window, SET semantics); the planner only needs the active voyage.
  useEffect(() => {
    if (!enabled || parsedVesselId == null) return;
    let active = true;
    (async () => {
      try {
        const data = await coreGet(
          `/voyages?vessel_id=${parsedVesselId}&type=planned&state=active`,
          accessRef.current.accessContext,
          accessRef.current.setAccessContext,
          ".core /voyages (planner) error=",
        );
        if (!active) return;
        upsertEntities(
          normalizeTreeMetadata({
            voyages: data?.voyages ?? [],
            vesselId: parsedVesselId,
          }),
        );
        setVoyagesLoadedFor(parsedVesselId);
        setVoyagesFailedFor(null);
      } catch (err) {
        if (!active) return;
        console.error("Failed to load voyage options for planner:", err);
        setVoyagesFailedFor(parsedVesselId);
      }
    })();
    return () => {
      active = false;
    };
  }, [enabled, parsedVesselId, refreshSignal, upsertEntities]);

  const voyageOptions = useMemo(
    () =>
      parsedVesselId == null
        ? []
        : selectVoyagesForVessel(state, parsedVesselId).filter(
            (voyage) => isPlannedEntity(voyage) && isActiveEntity(voyage),
          ),
    [state, parsedVesselId],
  );
  const voyage = voyageOptions[0] ?? null;
  const voyageId = toId(voyage?.id);

  useEffect(() => {
    if (!enabled || voyageId == null) return;
    let active = true;
    (async () => {
      try {
        const data = await coreGet(
          `/routes?voyage_id=${voyageId}&type=planned`,
          accessRef.current.accessContext,
          accessRef.current.setAccessContext,
          ".core /routes (planner) error=",
        );
        if (!active) return;
        setRoutesForVoyage(
          voyageId,
          normalizeTreeMetadata({ routes: data?.routes ?? [], voyageId }),
        );
      } catch (err) {
        if (!active) return;
        console.error("Failed to load planner voyage routes:", err);
      }
    })();
    return () => {
      active = false;
    };
  }, [enabled, voyageId, refreshSignal, setRoutesForVoyage]);

  const activeRouteId = useMemo(
    () =>
      voyageId == null
        ? null
        : toId(
            selectActiveOrSuggestedRoute(selectRoutesForVoyage(state, voyageId))
              ?.id,
          ),
    [state, voyageId],
  );

  const waypointRouteIds = useMemo(
    () => (enabled && activeRouteId != null ? [activeRouteId] : EMPTY_IDS),
    [enabled, activeRouteId],
  );
  useTreeRouteWaypoints({
    selectedRouteIds: waypointRouteIds,
    accessContext,
    setAccessContext,
    refreshNonce: refreshSignal,
  });

  // Keyed on the slices the selector reads, not on the whole store — the
  // points feed an interpolation in the panel and must not churn per dispatch.
  const routeWaypoints = state?.waypointsByRouteId?.[activeRouteId];
  const routeEntity = state?.entities?.routesById?.[activeRouteId];
  const activeRoutePoints = useMemo(
    () =>
      enabled && activeRouteId != null && routeWaypoints
        ? selectRoutePointsGeojson(
            {
              entities: { routesById: { [activeRouteId]: routeEntity } },
              waypointsByRouteId: { [activeRouteId]: routeWaypoints },
            },
            activeRouteId,
          )
        : null,
    [enabled, activeRouteId, routeWaypoints, routeEntity],
  );

  return {
    voyage,
    voyageOptions,
    voyagesLoaded: parsedVesselId != null && voyagesLoadedFor === parsedVesselId,
    voyagesFailed: parsedVesselId != null && voyagesFailedFor === parsedVesselId,
    activeRouteId,
    activeRoutePoints,
  };
}
